import type React from "react";
import { type ReactNode, createContext, useContext, useMemo, useState } from "react";

import type { CommonModels } from "@/data/common/common.models";
import type { FileUploadState } from "@/data/media/useFileUpload";

export type FileUploadItem = {
  file: File;
  id: string;
  status: FileUploadState;
};

export type UploadBatch = {
  id: string;
  files: FileUploadItem[];
  tags: CommonModels.BizTagDto[];
  uploadedMediaIds: string[];
  isProcessing: boolean;
  hasBeenProcessed: boolean;
  createdAt: Date;
  jobTitle: string;
  location: string;
};

type UploadsContextType = {
  // State
  isModalOpen: boolean;
  selectedFiles: File[];
  uploadQueue: UploadBatch[];
  currentUploadBatch: UploadBatch | null;
  showMinimizedWindow: boolean;

  // Actions
  setIsModalOpen: (isModalOpen: boolean) => void;
  setSelectedFiles: (selectedFiles: File[]) => void;
  addBatchToQueue: (batch: Omit<UploadBatch, "id" | "createdAt">) => string;
  updateBatchMetadata: (batchId: string, tags: CommonModels.BizTagDto[], jobPosition: string, location: string) => void;
  setBatchProcessing: (batchId: string, isProcessing: boolean) => void;
  removeBatchFromQueue: (batchId: string) => void;
  setCurrentUploadBatch: React.Dispatch<React.SetStateAction<UploadBatch | null>>;
  setShowMinimizedWindow: (show: boolean) => void;
  updateQueue: (batchId: string, updatedBatch: UploadBatch) => void;
  setUploadQueue: React.Dispatch<React.SetStateAction<UploadBatch[]>>;
};

const UploadsContext = createContext<UploadsContextType>({} as never);

export function UploadsProvider({ children }: { children: ReactNode }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadQueue, setUploadQueue] = useState<UploadBatch[]>([]);
  const [currentUploadBatch, setCurrentUploadBatch] = useState<UploadBatch | null>(null);
  const [showMinimizedWindow, setShowMinimizedWindow] = useState(false);

  const addBatchToQueue = (batch: Omit<UploadBatch, "id" | "createdAt">): string => {
    const batchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newBatch: UploadBatch = {
      ...batch,
      id: batchId,
      createdAt: new Date(),
    };
    setUploadQueue((prev) => [...prev, newBatch]);
    return batchId;
  };

  const updateBatchMetadata = (batchId: string, tags: CommonModels.BizTagDto[], jobTitle: string, location: string) => {
    setUploadQueue((prev) =>
      prev.map((batch) =>
        batch.id === batchId ? ({ ...batch, tags, jobTitle, location } satisfies UploadBatch) : batch,
      ),
    );
  };

  const setBatchProcessing = (batchId: string, isProcessing: boolean) => {
    setUploadQueue((prev) =>
      prev.map((batch) => (batch.id === batchId ? ({ ...batch, isProcessing } satisfies UploadBatch) : batch)),
    );
  };

  const removeBatchFromQueue = (batchId: string) => {
    setUploadQueue((prev) => prev.filter((batch) => batch.id !== batchId));
  };

  const updateQueue = (batchId: string, updatedBatch: UploadBatch) => {
    setUploadQueue((prev) => prev.map((batch) => (batch.id === batchId ? updatedBatch : batch)));
  };

  const value = useMemo(
    () => ({
      isModalOpen,
      selectedFiles,
      uploadQueue,
      currentUploadBatch,
      showMinimizedWindow,

      setIsModalOpen,
      setSelectedFiles,
      addBatchToQueue,
      updateBatchMetadata,
      setBatchProcessing,
      removeBatchFromQueue,
      setCurrentUploadBatch,
      setShowMinimizedWindow,
      updateQueue,
      setUploadQueue,
    }),
    [isModalOpen, selectedFiles, uploadQueue, currentUploadBatch, showMinimizedWindow],
  );

  return <UploadsContext.Provider value={value}>{children}</UploadsContext.Provider>;
}

export function ProfileUploadsContext() {
  const context = useContext(UploadsContext);

  return context;
}
